import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { clearViewAllLeadRow, fetchViewAllLeadRow, setViewAllLeadRow } from '../../../../../store/investor/action';

type Args = {
    assesseeOrgId: string | undefined;
    companyAccessType: string | undefined;
    investorOrgId: string | undefined;
};

// The row lives in redux (not local state) so `applyCompanyUpdate` patches it on drawer edits.
export function useCompanyLeadProperties({ assesseeOrgId, companyAccessType, investorOrgId }: Args) {
    const dispatch = useAppDispatch();
    const [settledKey, setSettledKey] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const leadRow = useAppSelector((state) => state.investor.viewAllProperties?.leadRow);

    const canFetch = !!(assesseeOrgId && companyAccessType && investorOrgId);
    const fetchKey = `${assesseeOrgId}|${companyAccessType}|${investorOrgId}`;
    // Derived, so the first render is already "loading" and never flashes "Lead not found".
    const loading = canFetch && settledKey !== fetchKey;

    useEffect(() => {
        if (!assesseeOrgId || !companyAccessType || !investorOrgId) return;

        let cancelled = false;
        setError(false);

        dispatch(
            fetchViewAllLeadRow(
                { assesseeOrgId, investorOrgId, companyAccessType },
                {
                    onSuccess: (row) => {
                        if (cancelled) return;
                        dispatch(setViewAllLeadRow(row));
                        setSettledKey(fetchKey);
                    },
                    onError: () => {
                        if (cancelled) return;
                        setError(true);
                        setSettledKey(fetchKey);
                    },
                },
            ),
        );

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assesseeOrgId, companyAccessType, investorOrgId]);

    useEffect(() => {
        return () => {
            dispatch(clearViewAllLeadRow());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        leadRow,
        loading,
        error,
        notFound: !loading && !error && !leadRow,
    };
}

export default useCompanyLeadProperties;
